import React from 'react';
import cn from 'classnames';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options?: readonly SelectOption[];
  placeholder?: string;
  children?: React.ReactNode;
}

const chevronSvg = encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>`
);

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      options,
      placeholder,
      children,
      defaultValue,
      value,
      ...props
    },
    ref
  ) => {
    const resolvedDefaultValue =
      value === undefined
        ? defaultValue !== undefined
          ? defaultValue
          : placeholder
            ? ''
            : undefined
        : undefined;

    return (
      <select
        className={cn(
          'h-10 w-full rounded-lg border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 pr-8 text-sm leading-normal text-slate-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:ring-offset-gray-900 disabled:cursor-not-allowed disabled:bg-slate-100 dark:disabled:bg-gray-800/60 disabled:text-slate-500 dark:disabled:text-gray-500 cursor-pointer appearance-none',
          className
        )}
        style={{
          backgroundImage: `url("data:image/svg+xml,${chevronSvg}")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 6px center',
          backgroundSize: '16px',
        }}
        ref={ref}
        defaultValue={resolvedDefaultValue}
        value={value}
        {...props}
      >
        {placeholder && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {options
          ? options.map((o) => (
              <option
                key={o.value}
                value={o.value}
                className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200"
              >
                {o.label}
              </option>
            ))
          : children}
      </select>
    );
  }
);

Select.displayName = 'Select';

export { Select };
